import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { DESTINATIONS, HERO_IMAGE } from "@/lib/data";
import { useCms } from "@/context/ContentContext";
import { waLink } from "@/lib/site";
import { dedupeBySlug } from "@/lib/cms";

const DEST_FAQS = [
  {
    q: "Do you sell directly to travellers?",
    a: "No. TripTrix Voyages is a B2B DMC — we work only with travel agents and tour operators. Your client stays yours; we handle the ground and stay behind your brand.",
  },
  {
    q: "How do agents get net rates?",
    a: "Send your dates, pax and destinations through the enquiry form or on WhatsApp and our trade desk replies within 24 hours with confidential net rates.",
  },
  {
    q: "Do you arrange permits like ILP and PAP?",
    a: "Yes. We arrange Inner Line Permits and Protected Area Permits for Arunachal Pradesh, Nagaland, Sikkim and other restricted areas as part of the ground handling.",
  },
  {
    q: "Which regions do you operate?",
    a: "Meghalaya, Assam, Arunachal Pradesh, Sikkim, Nagaland, Manipur, Mizoram, Tripura and Darjeeling, plus Bhutan and Nepal.",
  },
  {
    q: "Can you handle airport and railway transfers?",
    a: "Yes — meet-and-greet and private transfers from Bagdogra (IXB), Guwahati (GAU), NJP and Guwahati railway, coordinated with your itinerary.",
  },
];

export default function Destinations() {
  usePageMeta({
    title: "North East India, Bhutan, Nepal & Darjeeling Destinations | TripTrix DMC",
    description:
      "Every region we run as your ground DMC — Meghalaya, Assam, Arunachal, Sikkim, Nagaland & more, plus Bhutan, Nepal & Darjeeling. Net rates for agents.",
    keywords:
      "North East India destinations for travel agents, Meghalaya DMC, Assam DMC, Arunachal Pradesh DMC, Sikkim DMC, Nagaland DMC, Manipur Mizoram Tripura tour operator, Darjeeling DMC, Bhutan DMC, Nepal DMC, net rates North East India, B2B destinations North East India, ground handling North East India for agents",
  });
  const location = useLocation();
  const cms = useCms();
  const allDestinations = dedupeBySlug(DESTINATIONS, cms.destinations);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [location.hash]);

  return (
    <>
      <PageHeader
        overline="Where we operate"
        title="Destinations we run as your ground DMC"
        sub="These are the regions we work on the ground every season — our own coordinators, vetted drivers and the permits handled for you. Open any destination for highlights, the best months to travel and sample routes. Agents get confidential net rates; your clients only ever see your brand."
        image={HERO_IMAGE}
      />

      {/* B2B intro + internal links to money pages */}
      <section className="border-b border-[var(--tx-border)] bg-[var(--tx-bg-2)]" data-testid="destinations-intro">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12 md:py-16 grid gap-8 lg:grid-cols-12 items-start">
          <p className="lg:col-span-7 text-lg text-[var(--tx-text-2)] leading-relaxed">
            Every region below is one we run ourselves — permits, transport, stays and guides
            arranged by our own team on the ground. Pick a destination to see highlights, the
            best months to travel and sample routes, then send us a brief for net rates. If you
            plan by state, our <Link to="/northeast-dmc" className="text-[var(--tx-gold)] hover:underline">North East India DMC</Link> desk
            and <Link to="/tours" className="text-[var(--tx-gold)] hover:underline">tour packages</Link> are the quickest place to start.
          </p>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="destinations-cta-rates" className="inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-6 py-3 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
              Get net rates <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/partners" className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-6 py-3 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
              Why agents trust us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-28" data-testid="destinations-list">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 space-y-24 md:space-y-32">
          {allDestinations.map((d, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={d.slug}>
                <div id={d.slug} className="grid gap-10 lg:grid-cols-12 items-center scroll-mt-32">
                  <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
                    <Link to={`/destinations/${d.slug}`} className="group relative block h-[360px] md:h-[520px] overflow-hidden">
                      <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                      <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
                    </Link>
                  </div>
                  <div className={`lg:col-span-5 ${reversed ? "lg:order-1 lg:pr-10" : "lg:pl-10"}`}>
                    <span className="text-xs uppercase tracking-[0.24em] text-[var(--tx-text-2)]">{d.region}</span>
                    <div className="mt-3 text-[var(--tx-gold)] uppercase tracking-[0.2em] text-sm">{d.kicker}</div>
                    <h2 className="mt-4 font-display font-light tracking-tight text-4xl md:text-6xl">{d.name}</h2>
                    <p className="mt-6 text-lg text-[var(--tx-text-2)] leading-relaxed">{d.blurb}</p>
                    <div className="mt-8 flex items-center gap-4">
                      <Link to={`/destinations/${d.slug}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-6 py-3 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
                        Explore {d.name} <ArrowUpRight className="h-4 w-4" />
                      </Link>
                      <a href={waLink(`Hi TripTrix, I'm interested in a trip to ${d.name}.`)} target="_blank" rel="noreferrer" className="text-sm text-[var(--tx-text)]/80 hover:text-[var(--tx-gold)] transition-colors duration-300">
                        WhatsApp us
                      </a>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAQ + FAQPage schema */}
      <section className="border-t border-[var(--tx-border)] py-20 md:py-28" data-testid="destinations-faq">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          <h2 className="font-display font-light tracking-tight text-3xl md:text-4xl">Common questions from agents</h2>
          <div className="mt-10 divide-y divide-[var(--tx-border)]">
            {DEST_FAQS.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-display text-xl font-light text-[var(--tx-text)]">{f.q}</h3>
                <p className="mt-3 text-[var(--tx-text-2)] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <JsonLd
        id="jsonld-destinations-faq"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: DEST_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
    </>
  );
}
